import { Barra } from "./styled";

/**
 * Páginas a mostrar na barra: sempre a primeira e a última, a atual com um vizinho
 * de cada lado, e "..." (null) onde houver salto. Função pura, para testar sem tela.
 *
 *   atual=1,  total=20 -> [1, 2, 3, null, 20]
 *   atual=10, total=20 -> [1, null, 9, 10, 11, null, 20]
 *   atual=20, total=20 -> [1, null, 18, 19, 20]
 *   total <= 7        -> todas, sem reticências
 */
export function paginasVisiveis(atual, totalPaginas) {
  if (totalPaginas <= 7) return Array.from({ length: totalPaginas }, (_, i) => i + 1);

  const base = new Set([1, totalPaginas, atual - 1, atual, atual + 1]);
  // Nas pontas mostra um pouco mais, para a barra não "pular" de tamanho.
  if (atual <= 3) { base.add(2); base.add(3); base.add(4); }
  if (atual >= totalPaginas - 2) { base.add(totalPaginas - 1); base.add(totalPaginas - 2); base.add(totalPaginas - 3); }

  const ordenadas = [...base].filter((p) => p >= 1 && p <= totalPaginas).sort((a, b) => a - b);
  const resultado = [];
  ordenadas.forEach((p, i) => {
    if (i > 0 && p - ordenadas[i - 1] > 1) {
      // Um buraco de UMA página não vale reticências: mostra a página.
      if (p - ordenadas[i - 1] === 2) resultado.push(p - 1);
      else resultado.push(null);
    }
    resultado.push(p);
  });
  return resultado;
}

/** Faixa mostrada: "21–40" (vazio: "0"). */
export function faixaMostrada(pagina, porPagina, total) {
  if (total === 0) return "0";
  const de = (pagina - 1) * porPagina + 1;
  const ate = Math.min(pagina * porPagina, total);
  return `${de}–${ate}`;
}

/**
 * Barra de paginação das listas administrativas.
 *
 * Controlada: quem usa guarda `pagina` e `porPagina` e busca os dados. Acessível:
 * <nav>, página atual com aria-current, botões com rótulo completo. Só mostra os
 * controles quando há mais de uma página; o seletor de "por página" só quando a
 * lista passa do menor tamanho oferecido.
 */
export default function Paginacao({
  pagina, total, porPagina, onMudarPagina, onMudarPorPagina,
  opcoes = [10, 20, 50], itens = "itens",
}) {
  const totalPaginas = Math.max(1, Math.ceil(total / porPagina));
  const paginas = paginasVisiveis(pagina, totalPaginas);
  const irPara = (p) => { if (p >= 1 && p <= totalPaginas && p !== pagina) onMudarPagina(p); };

  return (
    <Barra>
      <span className="resumo-pagina" role="status">
        Mostrando {faixaMostrada(pagina, porPagina, total)} de {total} {itens}
      </span>

      {onMudarPorPagina && total > Math.min(...opcoes) &&
        <label className="por-pagina">
          Por página
          <select value={porPagina} onChange={(e) => onMudarPorPagina(Number(e.target.value))}>
            {opcoes.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
      }

      {totalPaginas > 1 &&
        <nav aria-label="Paginação" className="paginas">
          <button type="button" onClick={() => irPara(pagina - 1)} disabled={pagina === 1} aria-label="Página anterior">‹ Anterior</button>
          {paginas.map((p, i) => p === null
            ? <span key={`salto-${i}`} className="salto" aria-hidden="true">…</span>
            : (
              <button
                key={p} type="button"
                className={p === pagina ? "atual" : undefined}
                aria-current={p === pagina ? "page" : undefined}
                aria-label={`Ir para a página ${p}`}
                onClick={() => irPara(p)}
              >{p}</button>
            ))}
          <button type="button" onClick={() => irPara(pagina + 1)} disabled={pagina === totalPaginas} aria-label="Próxima página">Próxima ›</button>
        </nav>
      }
    </Barra>
  );
}
